import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import { CalendarDays, MapPin, Filter } from "lucide-react";

const CATEGORIES = ["All", "Economy", "Sedan", "Luxury", "SUV"];

export default function Fleet() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [pickup, setPickup] = useState(params.get("pickup") || "");
  const [dropoff, setDropoff] = useState(params.get("return") || "");

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (cat !== "All") q.category = cat;
    if (pickup && dropoff) {
      q.pickup = new Date(pickup).toISOString();
      q.dropoff = new Date(dropoff).toISOString();
    }
    api.get("/vehicles", { params: q })
      .then(({ data }) => setVehicles(data))
      .finally(() => setLoading(false));
  }, [cat, pickup, dropoff]);

  const applyDates = () => {
    const next = new URLSearchParams(params);
    if (pickup) next.set("pickup", pickup); else next.delete("pickup");
    if (dropoff) next.set("return", dropoff); else next.delete("return");
    setParams(next);
  };
  const clearDates = () => { setPickup(""); setDropoff(""); setParams({}); };

  return (
    <div className="bg-[#040B16] min-h-screen text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="eyebrow mb-3">The Fleet</div>
        <h1 className="font-display font-extrabold text-5xl sm:text-6xl tracking-tighter mb-4">Choose your next drive.</h1>
        <p className="text-white/60 max-w-2xl mb-10">From nimble economy runabouts to executive luxury sedans and commanding SUVs — every vehicle in our fleet is maintained to concierge standards.</p>

        {/* Availability picker */}
        <div className="bg-[#0B1F3A]/50 border border-white/10 rounded-2xl p-5 mb-8 grid md:grid-cols-4 gap-4 items-end">
          <Field icon={<CalendarDays className="w-4 h-4 text-[#D4AF37]" />} label="Pick-up date & time" type="datetime-local"
            value={pickup} onChange={setPickup} testId="fleet-pickup-date" />
          <Field icon={<CalendarDays className="w-4 h-4 text-[#D4AF37]" />} label="Return date & time" type="datetime-local"
            value={dropoff} onChange={setDropoff} testId="fleet-return-date" />
          <button onClick={applyDates} data-testid="fleet-apply-dates"
            className="btn-gold justify-center"><Filter className="w-4 h-4" /> Check availability</button>
          {(pickup || dropoff) && (
            <button onClick={clearDates} data-testid="fleet-clear-dates" className="btn-ghost justify-center">Clear dates</button>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              data-testid={`fleet-filter-${c.toLowerCase()}`}
              className={`px-5 py-2 rounded-full text-sm border transition-colors ${cat === c ? "bg-[#D4AF37] text-[#0B1F3A] border-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37] hover:text-white"}`}>{c}</button>
          ))}
        </div>

        {pickup && dropoff && (
          <div className="mb-6 text-sm text-[#D4AF37]" data-testid="fleet-availability-note">
            Showing vehicles available between {new Date(pickup).toLocaleString()} and {new Date(dropoff).toLocaleString()}
          </div>
        )}

        {loading ? (
          <div className="text-white/50">Loading fleet…</div>
        ) : vehicles.length === 0 ? (
          <div className="text-white/50 py-16 text-center border border-white/10 rounded-2xl">
            <MapPin className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            No vehicles available for these dates. Try a different date range or category.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</span>
      <div className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[#D4AF37] transition-colors">
        {icon}
        <input type={type} value={value} data-testid={testId}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm text-white [color-scheme:dark]" />
      </div>
    </label>
  );
}
